const DEFAULT_MAX_BYTES = 9.5 * 1024 * 1024;
export const IMAGE_UPLOAD_ACCEPT = 'image/*,.heic,.heif';

export class ImagePreparationError extends Error {
  constructor(message, code = 'image_preparation_failed') {
    super(message);
    this.name = 'ImagePreparationError';
    this.code = code;
  }
}

const canvasToBlob = (canvas, quality) => new Promise((resolve, reject) => {
  canvas.toBlob(
    (blob) => blob ? resolve(blob) : reject(new ImagePreparationError('The browser could not convert this image.')),
    'image/jpeg',
    quality,
  );
});

const loadWithImageElement = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => resolve({
    source: image,
    width: image.naturalWidth,
    height: image.naturalHeight,
    release: () => URL.revokeObjectURL(url),
  });
  image.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new ImagePreparationError(
      'This image cannot be opened by this browser. Please export it as JPEG and try again.',
      'unsupported_image_format',
    ));
  };
  image.src = url;
});

const decodeImage = async (file) => {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close(),
      };
    } catch (error) {
      // Safari can decode some camera formats through an image element even
      // when createImageBitmap cannot, so retain that browser-native fallback.
    }
  }
  return loadWithImageElement(file);
};

const outputName = (name = 'photo') => `${name.replace(/\.[^.]+$/, '') || 'photo'}.jpg`;

/**
 * Decode, orient, resize, strip metadata, and encode an uploaded image as JPEG.
 * Browser-decodable HEIC/HEIF files work without page-specific handling.
 */
export async function prepareImageForUpload(file, options = {}) {
  if (!(file instanceof Blob)) {
    throw new ImagePreparationError('Please choose an image file.', 'missing_image');
  }

  const maxDimension = options.maxDimension || 2560;
  const maxBytes = options.maxBytes || DEFAULT_MAX_BYTES;
  const decoded = await decodeImage(file);

  try {
    if (!decoded.width || !decoded.height) {
      throw new ImagePreparationError('The selected image has invalid dimensions.');
    }

    const initialScale = Math.min(1, maxDimension / Math.max(decoded.width, decoded.height));
    let width = Math.max(1, Math.round(decoded.width * initialScale));
    let height = Math.max(1, Math.round(decoded.height * initialScale));
    let quality = options.quality || 0.85;
    let blob;

    for (let attempt = 0; attempt < 8; attempt += 1) {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) throw new ImagePreparationError('Image conversion is unavailable in this browser.');
      context.fillStyle = '#fff';
      context.fillRect(0, 0, width, height);
      context.drawImage(decoded.source, 0, 0, width, height);
      blob = await canvasToBlob(canvas, quality);
      canvas.width = 1;
      canvas.height = 1;
      if (blob.size <= maxBytes) break;

      if (quality > 0.65) quality -= 0.08;
      else {
        width = Math.max(1, Math.round(width * 0.82));
        height = Math.max(1, Math.round(height * 0.82));
      }
    }

    if (!blob || blob.size > maxBytes) {
      throw new ImagePreparationError(
        'This image is still too large after optimization. Please choose a smaller image.',
        'image_too_large',
      );
    }

    return new File([blob], outputName(file.name), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } finally {
    decoded.release();
  }
}
